import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns } from "../../utils/preferencesUtils";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const SPECIAL_ITEM_SORT_FIELDS = {
  name: "MenuItem_Name",
  salePrice: "MenuItem_SalePrice",
  sortingOrder: "MenuItem_Orderby",
};

const SPECIAL_ITEM_SORT_LABELS = {
  [SPECIAL_ITEM_SORT_FIELDS.name]: "Name",
  [SPECIAL_ITEM_SORT_FIELDS.salePrice]: "Sale Price",
  [SPECIAL_ITEM_SORT_FIELDS.sortingOrder]: "Sorting Order",
};

const ManageSpecialItems = () => {
  const dispatch = useDispatch();

  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const compCurrency =
    useSelector((state) => state.auth.profileData?.compCurrency) || "GBP";

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [statusConfirmItem, setStatusConfirmItem] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsFetchSeqRef = useRef(0);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const currencySymbol = currencySymbols[compCurrency] || compCurrency;

  const pageKey = "specialItems";
  const defaultVisibleColumns = {
    name: true,
    salePrice: true,
    status: true,
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "MenuItem_Name", label: "Name" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: SPECIAL_ITEM_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const hasFiltersApplied = () => search.trim() !== "";

  const fetchSpecialItems = useCallback(async () => {
    if (!CompID) return;
    const fetchSeq = ++itemsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SPECIAL_MENU_ITEMS, {
        params: {
          CompID,
          PageNumber: pageNumber,
          PageSize: pageSize.value,
          Search: search?.trim() || null,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      if (fetchSeq !== itemsFetchSeqRef.current) return;

      const res = response.data || {};
      const data = res.data ?? res.Data ?? [];
      const list = Array.isArray(data) ? data : [];
      const records = res.totalRecords ?? res.TotalRecords ?? list.length ?? 0;
      const pages = res.totalPages ?? res.TotalPages ?? 1;

      setItems(list);
      setTotalRecords(records);
      setTotalPages(pages);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (fetchSeq !== itemsFetchSeqRef.current) return;
      setItems([]);
      setTotalRecords(0);
      setTotalPages(1);
      const msg =
        err.response?.data?.message ??
        err.response?.data?.Message ??
        "Failed to load special items";
      showErrorToast(msg);
    } finally {
      if (fetchSeq === itemsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy?.value, sortOrder?.value, pageNumber, pageSize?.value]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(itemsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useEffect(() => {
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useDebouncedEffect(() => fetchSpecialItems(), [fetchSpecialItems]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value, pageNumber, pageSize?.value]);

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No special items match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no special items configured yet. Use Add Special Item to add products.";
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Are you sure you want to remove this item from special items?")) {
      return;
    }

    try {
      setLoading(true);
      const menuItemId = item.MenuItem_ID ?? item.menuItem_ID ?? item.ID ?? item.id;
      
      const response = await axiosInstance.post(APIs.REMOVE_MENU_ITEM_FROM_SPECIAL, null, {
        params: {
          MenuItem_ID: menuItemId,
          CompID: item.CompID ?? item.compID ?? CompID,
        },
      });

      if (response.data && response.data.status !== false) {
        showSuccessToast("Item removed from special items successfully");
        fetchSpecialItems();
      } else {
        showErrorToast(response.data?.message || "Failed to remove special item");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to remove special item");
    }
  };

  const openCreateModal = () => {
    setSelectedProducts([]);
    setIsModalOpen(true);
  };

  const getItemPublishState = (item) =>
    !!(
      item.MenuItem_Publish ??
      item.menuItem_Publish ??
      item.Status ??
      item.status ??
      true
    );

  const getItemDisplayName = (item) =>
    item.MenuItem_Name ?? item.menuItem_Name ?? item.Name ?? item.name ?? "Item";

  const confirmSpecialItemStatusChange = async () => {
    if (!statusConfirmItem) return;

    const menuItemId =
      statusConfirmItem.MenuItem_ID ??
      statusConfirmItem.menuItem_ID ??
      statusConfirmItem.ID ??
      statusConfirmItem.id;
    const compId = statusConfirmItem.CompID ?? statusConfirmItem.compID ?? CompID;
    if (!menuItemId || !compId) return;

    const currentPublish = getItemPublishState(statusConfirmItem);
    const newPublish = !currentPublish;

    setStatusConfirmItem(null);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
        CompID: compId,
        MenuItem_ID: menuItemId,
        MenuItem_Publish: newPublish,
      });
      showSuccessToast(
        `"${getItemDisplayName(statusConfirmItem)}" is now ${newPublish ? "Active" : "InActive"}.`
      );
      fetchSpecialItems();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ??
          err.response?.data?.Message ??
          "Failed to update status"
      );
    }
  };

  const handleSave = async (selected) => {
    if (!CompID) {
      showErrorToast("Company not found.");
      return;
    }

    const productIds = (selected || [])
      .map((p) =>
        typeof p === "object"
          ? p.menuItem_ID ?? p.MenuItem_ID ?? p.id
          : p
      )
      .filter((id) => id != null && Number(id) > 0);

    if (productIds.length === 0) {
      showErrorToast("Please select at least one product.");
      return;
    }

    try {
      setLoading(true);
      
      // Process all selected products
      const promises = productIds.map(async (productId) => {
        const response = await axiosInstance.post(APIs.MAKE_MENU_ITEM_SPECIAL, null, {
          params: {
            MenuItem_ID: productId,
            CompID: CompID,
          },
        });
        return response;
      });

      const results = await Promise.all(promises);
      const allSuccess = results.every(r => r.data && r.data.status !== false);

      if (allSuccess) {
        const count = productIds.length;
        showSuccessToast(
          count === 1 
            ? "Item added to special items successfully" 
            : `${count} items added to special items successfully`
        );
        setIsModalOpen(false);
        setSelectedProducts([]);
        fetchSpecialItems();
      } else {
        showErrorToast("Some items failed to add. Please try again.");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to add special items");
    }
  };


  const renderNoDataMessage = () => {
    if (loading || items.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No special items found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
<div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Special Items</li>
          </ol>
          <h1 className="page-header mb-0">Special Items</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={openCreateModal}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Special Item
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <GridEmptyState
            isDarkMode={isDarkMode}
            themeColor={themeColor}
            title="No special items yet"
            description="Add products as special items to highlight them on your menu."
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Special Items"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name, category..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
                defaultColumns={defaultVisibleColumns}
                pageKey={pageKey}
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && items.length > 0 && (
                  <div
                    className="table-responsive table-container mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "600px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={SPECIAL_ITEM_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Item Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.salePrice && (
                            <SortableColumnHeader
                              sortField={SPECIAL_ITEM_SORT_FIELDS.salePrice}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Sale Price
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.status && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr
                            key={
                              item.MenuItem_ID ??
                              item.menuItem_ID ??
                              item.ID ??
                              item.id
                            }
                          >
                            {visibleColumns.name && (
                              <td className="align-middle">
                                {item.MenuItem_Name ??
                                  item.menuItem_Name ??
                                  item.Name ??
                                  item.name}
                              </td>
                            )}
                            {visibleColumns.salePrice && (
                              <td className="align-middle text-center">
                                {(() => {
                                  const p =
                                    item.MenuItem_SalePrice ??
                                    item.menuItem_SalePrice ??
                                    item.Price ??
                                    item.price;
                                  return typeof p === "number"
                                    ? `${currencySymbol}${p.toFixed(2)}`
                                    : p;
                                })()}
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td className="align-middle text-center">
                                <StatusBadge
                                  isActive={getItemPublishState(item)}
                                  onClick={() => setStatusConfirmItem(item)}
                                />
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDelete(item)}
                                >
                                  Remove from Special Items
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <ProductMultiSelectionModal
        showModal={isModalOpen}
        setShowModal={setIsModalOpen}
        showButton={true}
        onSubmit={handleSave}
        setSelectedProducts={setSelectedProducts}
        selectedProducts={selectedProducts}
        excludeIds={items.map(
          (item) => item.MenuItem_ID ?? item.menuItem_ID ?? item.ID ?? item.id
        )}
        maxSelection={null}
      />

      <CustomModal
        title="Change item status?"
        subtitle={
          statusConfirmItem
            ? `Are you sure you want to set "${getItemDisplayName(statusConfirmItem)}" to ${
                getItemPublishState(statusConfirmItem) ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmItem(null)}
        isOpen={!!statusConfirmItem}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmSpecialItemStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageSpecialItems;


